import React, { useEffect, useMemo, useRef } from "react";
import { Outlet } from "react-router-dom";
import {
  useGetAllcountsQuery,
  useLazyGetAwardsQuery,
  useLazyGetRecentAwardeesQuery,
} from "../../services/rtkQueryServices/checkApiService";
import useAwardSearch from "./useAwardSearch";
import SearchShell from "./SearchShell";
import { useThemeMode } from "../../theme/ThemeContext";
import "./AwardsSearch.scss";

// Persistent layout for the awards search family. It owns BOTH the awards and
// people queries so the toggle counts stay live and the data survives while the
// user switches tabs or pages - only the <Outlet/> below the shell re-renders.
export default function AwardsSearchLayout() {
  const { params, setParam } = useAwardSearch();
  const { isUta } = useThemeMode();
  const { data: countData } = useGetAllcountsQuery();

  const isAwards = params.type !== "awardees";

  // The Awardees tab opens on a shuffled sample; searching, facetting, sorting or the UTA filter turns it into an alphabetical search.
  const isPeopleDefaultView =
    !isAwards && !params.fn && !params.ln && !params.acad && !params.year && !params.sort && !(params.uta && isUta);

  // Decided during the first render, not in the effect: the people query fires first, and a late seed would fetch once then reshuffle.
  const seedRef = useRef(null);
  if (seedRef.current === null) seedRef.current = String(Math.floor(Math.random() * 2147483647) + 1);
  const shuffleSeed = params.seed || seedRef.current;

  useEffect(() => {
    // One seed per visit, written to the URL so refresh and Back reproduce the same order.
    if (isPeopleDefaultView && !params.seed) setParam("seed", shuffleSeed, { replace: true });
  }, [isPeopleDefaultView, params.seed, shuffleSeed, setParam]);

  const awardsFilter = useMemo(
    () => ({
      pageNumber: isAwards ? params.page : 1,
      pageSize: params.size,
      awardName: params.q || undefined,
      prestige: params.aau ? "AAU" : undefined,
      // UTA-faculty filter is a UTA-network feature; ignore the URL flag otherwise.
      utaFaculty: params.uta && isUta ? true : undefined,
      category: params.cat || undefined,
      level: params.level || undefined,
      kind: params.kind || undefined,
      organization: params.org || undefined,
      // No default sort here on purpose: the awards backend's own default
      // sorts alphabetically (Award ASC) while floating UTA-faculty awards to
      // the top for the UTA network. Sending an explicit sort bypasses that
      // UNION and drops the priority, so only forward a user-chosen sort.
      sortByColumnName: isAwards ? params.sort || undefined : undefined,
      sortOrder: isAwards ? params.dir || undefined : undefined,
    }),
    [params, isAwards, isUta]
  );

  const peopleFilter = useMemo(
    () => ({
      // landing:false -> real paginated search with the true total count.
      // (Omitting it defaults to the homepage widget: 20 random people, total=20.)
      landing: false,
      pageNumber: !isAwards ? params.page : 1,
      pageSize: params.size,
      // Separate first/last-name boxes; the backend ANDs them.
      firstName: params.fn || undefined,
      lastName: params.ln || undefined,
      academy: params.acad || undefined,
      year: params.year || undefined,
      // Same rule as the awards tab: the UTA flag is a UTA-network feature, ignore it elsewhere.
      utaFaculty: params.uta && isUta ? true : undefined,
      // Alphabetical by FullName once searching or sorting; the backend's NameLast default is dirty and reads as unsorted. No sort at all means the shuffled default view.
      sortByColumnName: !isAwards && !isPeopleDefaultView ? params.sort || "fullName" : undefined,
      sortOrder: !isAwards && !isPeopleDefaultView ? params.dir || "asc" : undefined,
      seed: isPeopleDefaultView ? shuffleSeed : undefined,
    }),
    [params, isAwards, isUta, isPeopleDefaultView, shuffleSeed]
  );

  const [fetchAwards, { data: awardsData, isFetching: awardsLoading }] =
    useLazyGetAwardsQuery();
  const [fetchPeople, { data: peopleData, isFetching: peopleLoading }] =
    useLazyGetRecentAwardeesQuery();

  useEffect(() => {
    fetchAwards(awardsFilter);
  }, [fetchAwards, awardsFilter]);
  useEffect(() => {
    fetchPeople(peopleFilter);
  }, [fetchPeople, peopleFilter]);

  return (
    <div className="p-3 h-100 overflow-auto awards-landing">
      <SearchShell
        awardsCount={awardsData?.totalRecords}
        peopleCount={peopleData?.totalRecords}
        aauCount={countData?.aau_highly_prestigious_count}
      />

      <Outlet
        context={{ awardsData, awardsLoading, peopleData, peopleLoading }}
      />
    </div>
  );
}
